/**
 * Mongo-first front tunables from Chat API (/runtime-env/front-config), merged in-memory.
 * Fallback: CRA-inlined process.env.REACT_APP_* (build-time).
 */

const merged: Record<string, unknown> = {};
const listeners = new Set<() => void>();

let loadInFlight: Promise<void> | null = null;

function emitUpdated(): void {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      // ignore subscriber errors
    }
  });
}

//function to subscribe to the front runtime config (means that when the front runtime config changes, the listener will be called)
export function subscribeFrontRuntimeConfig(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

//function to coerce the number (means that if the number is not a number, it will be converted to a number)
function coerceNumber(raw: unknown, fallback: number): number {
  if (raw === undefined || raw === null) {
    return fallback;
  }
  if (typeof raw === "number") {
    return Number.isFinite(raw) ? raw : fallback;
  }
  if (typeof raw === "string") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  }
  if (typeof raw === "boolean") {
    return raw ? 1 : 0;
  }
  return fallback;
}

/**
 * Numeric tunable: Mongo (merged) → CRA build-time env → literal fallback.
 */
export function frontEnvNumber(key: string, buildFallback: number): number {
  const raw = process.env[key as keyof NodeJS.ProcessEnv];
  let fb = buildFallback;
  if (raw !== undefined && raw !== "") {
    const n = Number(raw);
    if (Number.isFinite(n)) fb = n;
  }
  return getFrontRuntimeNumber(key, fb);
}

/**
 * Read a numeric front env: merged API payload first, then build-time env.
 */
/** String tunable: Mongo (merged) → CRA build-time env → literal fallback. */
export function frontEnvString(key: string, buildFallback: string): string {
  if (Object.prototype.hasOwnProperty.call(merged, key)) {
    const v = merged[key];
    if (typeof v === "boolean") return v ? "true" : "false";
    if (typeof v === "string" && v.trim()) return v;
    if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean).join(",");
  }
  const fromBuild = process.env[key as keyof NodeJS.ProcessEnv];
  if (typeof fromBuild === "string" && fromBuild.trim()) return fromBuild;
  return buildFallback;
}

export function getFrontRuntimeNumber(key: string, fallback: number): number {
  if (Object.prototype.hasOwnProperty.call(merged, key)) {
    return coerceNumber(merged[key], fallback);
  }
  const fromBuild = process.env[key as keyof NodeJS.ProcessEnv];
  if (fromBuild === undefined || fromBuild === "") {
    return fallback;
  }
  const n = Number(fromBuild);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Fetch allowlisted keys from Chat backend and merge into `merged`.
 * Safe to call on mount, on visibility, or after dashboard refresh (once Nest cache reloaded).
 */
export function loadFrontRuntimeConfig(): Promise<void> {
  if (loadInFlight) {
    return loadInFlight;
  }
  loadInFlight = (async () => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    if (!base) {
      return;
    }
    try {
      const res = await fetch(`${base}/runtime-env/front-config`);
      if (!res.ok) {
        return;
      }
      const body = (await res.json()) as {
        configured?: boolean;
        values?: Record<string, unknown>;
      };
      if (!body?.configured || !body.values) {
        return;
      }
      for (const [k, v] of Object.entries(body.values)) {
        merged[k] = v;
      }
      emitUpdated();
    } catch {
      // non-fatal: keep build-time fallbacks
    } finally {
      loadInFlight = null;
    }
  })();
  return loadInFlight;
}
